// VISTA EN SALA - kiosko de 2 puertas (docs/SPEC_VISTA_EN_SALA.md).
//
// Pantalla cerrada para la tablet de sala (Muntaner / Mefir). Sin menu del CRM,
// sin navegacion al resto. Dos puertas en orden estricto:
//   Puerta 1 - la SALA (dispositivo): usuario muntaner|mefir + contrasena
//              compartida -> abre la tablet en modo sala (sesion limitada 24h).
//   Puerta 2 - la PERSONA: desplegable de tecnicos autorizados (del endpoint del
//              Guardian, NO hardcodeado) + entrada "Visitante" (sustituto puntual,
//              clave que mete recepcion) -> teclea su contrasena del CRM (o la
//              clave de Visitante) -> sesion "de sala" a su nombre (identidad/firma).
//   "Cerrar sesion de tecnico" vuelve al desplegable SIN cerrar la sala.
//
// Dentro de la sesion: dos secciones -> ASISTENCIA (reusa <PasarLista/>) y CLASE
// (placeholder de lectura futura; el modulo de diseno de clase NO se construye aqui).
//
// ALCANCE v1: solo entran usuarios que YA estan en el CRM (+ Visitante). No se
// construyen tipos de cuenta nuevos.
//
// FRONTERA GUARDIAN: la verificacion de auth, el scope solo-sala, el lockout, la
// clave de Visitante y la lista de autorizados los publica el Guardian. Aqui NO se
// simula un login correcto con datos falsos: si el endpoint no existe, se muestra un
// estado honesto "pendiente". Para revisar el ESTILO sin backend existe un modo
// maqueta EXPLICITO (?maqueta=1), siempre con banner visible.

import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  abrirSala,
  listarTecnicosAutorizados,
  verificarTecnico,
} from "../../api/controlAsistencia";
import { AuthContext } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import PasarLista from "./PasarLista";
import ClasePlaceholder from "./ClasePlaceholder";
import "../control-asistencia.css";
import "./vista-en-sala.css";

const VISITANTE = "visitante";
const colorDeSede = (sede) =>
  String(sede || "").toUpperCase() === "MEFIR" ? "var(--sede-mefir)" : "var(--sede-muntaner)";

// Modo maqueta EXPLICITO (revision de estilo sin backend). Nunca en el flujo real.
const ES_MAQUETA = (() => {
  try { return new URLSearchParams(window.location.search).get("maqueta") === "1"; }
  catch { return false; }
})();

// Persistencia de la PUERTA entre recargas (F5) para no expulsar al usuario.
// Solo estado de UI (sede + identidad) - NUNCA contrasenas/secretos. El Guardian
// lo sustituye por el token real de sesion de sala (scope sala, 24h).
const SS_SALA = "vs_sala";
const SS_TEC = "vs_tecnico";
const _read = (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch { return null; } };
const _write = (k, v) => { try { v ? sessionStorage.setItem(k, JSON.stringify(v)) : sessionStorage.removeItem(k); } catch { /* noop */ } };

const VistaSalaShell = () => {
  // Puerta 1 (sala) - se rehidrata de sessionStorage al recargar.
  const [sala, setSala] = useState(() => _read(SS_SALA)); // { sede }
  const [u1, setU1] = useState("");
  const [p1, setP1] = useState("");
  const [err1, setErr1] = useState("");
  const [pend1, setPend1] = useState(false);
  const [busy1, setBusy1] = useState(false);

  // Puerta 2 (persona)
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicosPend, setTecnicosPend] = useState(false);
  const [sel, setSel] = useState("");          // id de tecnico, o VISITANTE
  const [p2, setP2] = useState("");
  const [err2, setErr2] = useState("");
  const [pend2, setPend2] = useState(false);
  const [busy2, setBusy2] = useState(false);
  const [persona, setPersona] = useState(() => _read(SS_TEC)); // { nombre, esVisitante }

  const [seccion, setSeccion] = useState("asistencia"); // 'asistencia' | 'clase'

  // ── MODO CRM ──────────────────────────────────────────────────────────────
  // Si quien abre la Vista de Sala YA está logueado en el CRM (entra desde la
  // pestaña Semanales), NO se le piden las 2 puertas: ya está autenticado y su
  // identidad/autoría la pone el token del CRM en el backend. Las 2 puertas
  // (usuario de sala + contraseña) quedan SOLO para la tablet sin sesión CRM
  // (kiosko · FASE B). No se debilita nada: el backend sigue exigiendo el
  // permiso `asistencia.control` en cada llamada.
  const { user, isAdmin } = useContext(AuthContext) || {};
  const modoCRM = !!user && !ES_MAQUETA;
  const puedePasarLista = isAdmin || user?.permisos_granulares?.["asistencia.control"]?.["ver"] === true;

  useEffect(() => { _write(SS_SALA, sala); }, [sala]);
  useEffect(() => { _write(SS_TEC, persona); }, [persona]);

  const cargarTecnicos = useCallback(async (sede) => {
    const r = await listarTecnicosAutorizados({ sede });
    setTecnicos(r.tecnicos || []);
    setTecnicosPend(!!r.pendiente);
    setSel("");
  }, []);

  useEffect(() => {
    if (sala?.sede && !persona && !ES_MAQUETA) cargarTecnicos(sala.sede);
  }, [sala, persona, cargarTecnicos]);

  // Puerta 1
  const onAbrirSala = async (e) => {
    e.preventDefault();
    setErr1(""); setPend1(false); setBusy1(true);
    try {
      const r = await abrirSala({ usuario: u1.trim(), password: p1 });
      if (r.pendiente) { setPend1(true); return; }
      if (!r.ok) { setErr1(r.error || "No se pudo abrir la sala."); return; }
      setSala({ sede: r.sede });
      setP1("");
    } finally { setBusy1(false); }
  };

  // Puerta 2
  const esVisitante = sel === VISITANTE;
  const onEntrar = async (e) => {
    e.preventDefault();
    setErr2(""); setPend2(false);
    if (!sel) { setErr2("Elige tu nombre."); return; }
    setBusy2(true);
    try {
      const r = await verificarTecnico({ sede: sala.sede, tecnico_id: sel, password: p2 });
      if (r.pendiente) { setPend2(true); return; }
      if (!r.ok) { setErr2(r.error || "No se pudo verificar."); return; }
      const nombre = esVisitante
        ? "Visitante"
        : (r.nombre || tecnicos.find((t) => t.id === sel)?.nombre || "Tecnico");
      setPersona({ nombre, esVisitante });
      setSeccion("asistencia");
      setP2("");
    } finally { setBusy2(false); }
  };

  const cerrarPersona = () => { setPersona(null); setP2(""); setErr2(""); setPend2(false); setSeccion("asistencia"); };
  const salirDeSala = () => { setPersona(null); setSala(null); setU1(""); setP1(""); };

  const entrarMaqueta = () => { setSala({ sede: u1.toUpperCase() === "MEFIR" ? "MEFIR" : "MUNTANER" }); setPersona({ nombre: "Maqueta", esVisitante: false }); };

  // ─────────────── MODO CRM (logueado · sin puertas) ───────────────
  // Entrada desde el CRM (p.ej. botón "Vista de Sala" en Semanales). Sin
  // contraseña: el usuario ya está autenticado. Selección de sede dentro de
  // <PasarLista/> (no se fuerza sedeFija). Identidad = usuario del CRM.
  if (modoCRM) {
    if (!puedePasarLista) {
      return (
        <div className="ca-app">
          <div className="ca-gate">
            <h2>Vista de Sala</h2>
            <p>No tienes permiso para pasar lista. Pide acceso al administrador
            (permiso <code>Control de Asistencia · ver</code>).</p>
          </div>
        </div>
      );
    }
    return (
      <div className="ca-app vs-sesion">
        <div className="vs-barra">
          <div className="vs-barra-id">
            Vista de Sala · <b>{user.nombre || user.email}</b>
          </div>
          <div className="vs-tabs">
            <button className={`vs-tab ${seccion === "asistencia" ? "activa" : ""}`} onClick={() => setSeccion("asistencia")}>ASISTENCIA</button>
            <button className={`vs-tab ${seccion === "clase" ? "activa" : ""}`} onClick={() => setSeccion("clase")}>CLASE</button>
          </div>
        </div>
        {seccion === "asistencia" ? <PasarLista /> : <ClasePlaceholder />}
      </div>
    );
  }

  // ───────────────────────── PUERTA 1 ─────────────────────────
  if (!sala) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <img src="/logo-mpd.png" alt="Metodo Pilar Dominguez" className="h-20 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Vista en sala</CardTitle>
            <CardDescription className="text-center">Puerta 1 · abrir la sala (dispositivo)</CardDescription>
          </CardHeader>
          <CardContent>
            {/* autoComplete desactivado: tablet COMPARTIDA -> el navegador NO debe
                recordar/autollenar credenciales (seguridad). */}
            <form onSubmit={onAbrirSala} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="vs-usuario">Usuario de sala</Label>
                <Input id="vs-usuario" placeholder="muntaner / mefir" value={u1}
                  onChange={(e) => setU1(e.target.value)} autoCapitalize="none" autoCorrect="off"
                  autoComplete="off" name="vs-usuario" data-1p-ignore data-lpignore="true" spellCheck={false} autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vs-pass-sala">Contrasena de sala</Label>
                <Input id="vs-pass-sala" type="password" placeholder="Contrasena" value={p1}
                  onChange={(e) => setP1(e.target.value)}
                  autoComplete="new-password" name="vs-pass-sala" data-1p-ignore data-lpignore="true" spellCheck={false} />
              </div>
              {err1 && <p className="text-sm text-red-600">{err1}</p>}
              {pend1 && <p className="text-sm text-amber-600">El acceso de sala lo publica el Guardian (endpoint pendiente).</p>}
              <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-600" disabled={busy1 || !u1 || !p1}>
                {busy1 ? "Abriendo..." : "Abrir sala"}
              </Button>
              {ES_MAQUETA && (
                <Button type="button" variant="outline" className="w-full" onClick={entrarMaqueta}>
                  Ver maqueta (sin backend)
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ───────────────────── PUERTA 2 ─────────────────────
  if (!persona) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <img src="/logo-mpd.png" alt="Metodo Pilar Dominguez" className="h-20 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Sala {sala.sede}</CardTitle>
            <CardDescription className="text-center">Puerta 2 · identificate</CardDescription>
          </CardHeader>
          <CardContent>
            {/* autoComplete desactivado: tablet COMPARTIDA -> nunca recordar la
                contrasena del tecnico ni la clave de Visitante. */}
            <form onSubmit={onEntrar} className="space-y-4" autoComplete="off">
              {tecnicosPend && (
                <p className="text-sm text-amber-600">La lista de tecnicos autorizados la publica el Guardian (endpoint pendiente).</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="vs-persona">Quien entra</Label>
                <Select value={sel} onValueChange={setSel}>
                  <SelectTrigger id="vs-persona">
                    <SelectValue placeholder="Elige tu nombre" />
                  </SelectTrigger>
                  <SelectContent>
                    {tecnicos.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                    ))}
                    <SelectItem value={VISITANTE}>Visitante (sustituto puntual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vs-pass-p2">{esVisitante ? "Clave de Visitante" : "Tu contrasena"}</Label>
                <Input id="vs-pass-p2" type="password"
                  placeholder={esVisitante ? "Clave que facilita recepcion" : "Contrasena"}
                  value={p2} onChange={(e) => setP2(e.target.value)}
                  autoComplete="new-password" name="vs-pass-p2" data-1p-ignore data-lpignore="true" spellCheck={false} />
                {esVisitante && (
                  <p className="text-xs text-slate-500">La clave de Visitante la mete recepcion (responsable de la entrada). Se firma como "Visitante · {sala.sede} · fecha/hora".</p>
                )}
              </div>
              {err2 && <p className="text-sm text-red-600">{err2}</p>}
              {pend2 && <p className="text-sm text-amber-600">La verificacion de la persona la publica el Guardian (endpoint pendiente).</p>}
              <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-600" disabled={busy2 || !sel || !p2}>
                {busy2 ? "Entrando..." : "Entrar"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={salirDeSala}>
                Cerrar sala
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ───────────────── SESION: ASISTENCIA / CLASE ────────────────
  return (
    <div className="ca-app vs-sesion" style={{ "--vs-sede": colorDeSede(sala.sede) }}>
      {(ES_MAQUETA || persona.nombre === "Maqueta") && (
        <div className="vs-maqueta-banner">MAQUETA · datos de ejemplo para revisar el estilo · sin backend</div>
      )}
      <div className="vs-barra">
        <div className="vs-barra-id">
          Sala <b>{sala.sede}</b> · {persona.esVisitante ? <b>Visitante</b> : <>tecnico: <b>{persona.nombre}</b></>}
        </div>
        <div className="vs-tabs">
          <button className={`vs-tab ${seccion === "asistencia" ? "activa" : ""}`} onClick={() => setSeccion("asistencia")}>ASISTENCIA</button>
          <button className={`vs-tab ${seccion === "clase" ? "activa" : ""}`} onClick={() => setSeccion("clase")}>CLASE</button>
        </div>
        <button className="vs-cerrar-tec" onClick={cerrarPersona}>Cerrar sesion de {persona.esVisitante ? "visitante" : "tecnico"}</button>
      </div>

      {seccion === "asistencia"
        ? <PasarLista sedeFija={sala.sede} maqueta={ES_MAQUETA || persona.nombre === "Maqueta"} />
        : <ClasePlaceholder />}
    </div>
  );
};

export default VistaSalaShell;
